import React, { useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Community, EventFundraiser } from "@dreambigger/shared/src/types";
import Decimal from "decimal.js";

type MatchGrantBannerProps = {
  campaign: EventFundraiser;
  community: Community;
  eventId: number;
  orgName?: string | null;
  donationBufferExpired?: boolean;
};

const MatchGrantBanner = ({
  campaign,
  community,
  eventId,
  orgName,
  donationBufferExpired,
}: MatchGrantBannerProps) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  // Use orgName or fallback to community organization name
  const displayOrgName =
    orgName || community.organization?.name || "the sponsor";

  // Determine match type and status
  const isDollarForDollar =
    campaign.thresholdTrigger === "one_to_one_match_trigger";
  const isDonationThreshold = campaign.thresholdTrigger === "donation_trigger";
  const isTieredMatch = campaign.thresholdTrigger === "tiered_match_trigger";
  const matchFundsRemaining = campaign.matchFundsRemaining || 0;
  const nextThreshold = campaign.nextThreshold;

  // Dollar-for-dollar: fully unlocked when matchFundsRemaining === 0
  // Legacy: campaignDollarbyDollarMatchCrossed = campaign.matchFundsRemaining === 0
  const dollarForDollarCrossed =
    isDollarForDollar && campaign.matchFundsRemaining === 0;

  // Tiered match: fully unlocked when matchFundsRemaining === 0
  // Legacy: campaignMatchbyDonationMatchCrossed = campaign.matchFundsRemaining === 0
  const tieredMatchCrossed =
    isTieredMatch && campaign.matchFundsRemaining === 0;

  // Donation threshold: all goals reached when no next threshold exists
  // Legacy: allgoalreached = every threshold has reachedAt property
  // API equivalent: nextThreshold absent = all thresholds reached
  const allThresholdsReached = isDonationThreshold && !nextThreshold;

  // Combined fully-unlocked state
  const isFullyUnlocked =
    dollarForDollarCrossed || tieredMatchCrossed || allThresholdsReached;
  const neededToUnlock = nextThreshold
    ? Decimal.max(
        new Decimal(nextThreshold.goal).minus(campaign.totalDonated || "0"),
        0
      ).toNumber()
    : 0;

  // Don't show banner if no matching is configured
  if (!isDollarForDollar && !isDonationThreshold && !isTieredMatch) return null;

  // Legacy: only the active (not crossed) dollar-for-dollar banner hides after buffer expires
  if (isDollarForDollar && !dollarForDollarCrossed && donationBufferExpired)
    return null;

  const handleClose = () => {
    setIsVisible(false);
  };

  const donateUrl = `/events/${eventId}/donate`;
  const communityUrl = `/community/${community.slug}/detail`;

  // Determine banner text
  let bannerText: string;
  if (isFullyUnlocked) {
    bannerText =
      "100% of match funds have been unlocked! You can still donate, or find another campaign.";
  } else if (isDonationThreshold && nextThreshold) {
    bannerText = `Only $${neededToUnlock.toLocaleString()} more needed to unlock $${nextThreshold.reward.toLocaleString()} in match grants!`;
  } else if (isDonationThreshold) {
    bannerText = `Donate to help unlock match grants from ${displayOrgName}!`;
  } else if (isDollarForDollar) {
    bannerText = `Every donation matched $1 for $1 by ${displayOrgName}! $${matchFundsRemaining.toLocaleString()} in match funds remaining.`;
  } else if (isTieredMatch) {
    bannerText = `$${matchFundsRemaining.toLocaleString()} in match funds remaining for this campaign!`;
  } else {
    bannerText = "";
  }

  // Determine button
  const buttonText = isFullyUnlocked ? "VIEW CAMPAIGNS" : "DONATE NOW";
  const buttonUrl = isFullyUnlocked ? communityUrl : donateUrl;

  // Green when fully unlocked, steel blue when active/in-progress
  const backgroundColor = isFullyUnlocked ? "#8DC756" : "#6fb2dd";

  return (
    <div
      className="w-100 mb-4 flex items-center"
      style={{
        backgroundColor,
        padding: "12px 24px",
        color: "white",
      }}
    >
      <div className="flex-1 f-4" style={{ lineHeight: "1.4" }}>
        {bannerText}
      </div>
      <a
        href={buttonUrl}
        className="fwb ttu f-3 flex items-center justify-center pointer"
        style={{
          color: "white",
          border: "2px solid white",
          borderRadius: "4px",
          padding: "8px 20px",
          textDecoration: "none",
          whiteSpace: "nowrap",
          marginLeft: "16px",
        }}
      >
        {buttonText}
      </a>
      <button
        onClick={handleClose}
        className="bg-transparent pointer flex items-center justify-center"
        style={{
          border: "none",
          outline: "none",
          color: "white",
          marginLeft: "16px",
          padding: "4px",
          fontSize: "16px",
        }}
        aria-label="Close banner"
      >
        <CloseOutlined />
      </button>
    </div>
  );
};

export default MatchGrantBanner;
